import React, { useEffect, useRef, useState } from 'react';
import BookCallModal from '../../components/BookCallModal';
import ScoreDial, { gradeColor } from './ScoreDial';
import FloorScale from './FloorScale';
import IssueList from './IssueList';
import ComplianceSheet from './ComplianceSheet';
import { BTN_GHOST, BTN_PRIMARY, EvTag, PANEL, PANEL_TITLE, SectionHead } from './parts';
import './siteCheck.css';

const MAX_PAGES = 7;
const COPY_RESET_MS = 1800;
const CARD = 'grid content-start gap-2.5 rounded-[18px] border p-5';
const CARD_TITLE = 'm-0 font-display text-lg font-semibold';
const CARD_TEXT = 'm-0 text-sm text-mist';
const CHIP = 'rounded-full border px-[11px] py-[5px] text-[13px]';
const CHIP_OK = `${CHIP} border-[rgba(61,214,140,0.35)] text-[#3DD68C]`;
const CHIP_MISS = `${CHIP} border-dashed border-white/20 text-mist`;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const townOf = (city) => String(city || '').split(',')[0].trim();

function industryLabel(key, industries) {
  if (!key) return 'Local business';
  const match = (industries || []).find((option) => option.key === key);
  if (match) return match.label;
  const words = String(key).replace(/[-_]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatDate(iso) {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function floorVerdict(score, floorScore) {
  const behind = floorScore - score;
  if (behind > 0) return `, ${plural(behind, 'point')} under the floor.`;
  if (behind === 0) return ', right at the floor.';
  return ', above the floor.';
}

// Copies the share link; when the clipboard is blocked it selects the link
// text so the visitor can copy it by hand.
function CopyButton({ url, onFallback }) {
  const [label, setLabel] = useState('Copy link');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text) => {
    setLabel(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setLabel('Copy link'), COPY_RESET_MS);
  };
  const fallback = () => { onFallback(); flash('Link selected'); };
  const copy = () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) { fallback(); return; }
      navigator.clipboard.writeText(url).then(() => flash('Copied'), fallback);
    } catch {
      fallback();
    }
  };

  return <button type="button" onClick={copy} className={BTN_GHOST}>{label}</button>;
}

function Kpi({ label, value, note }) {
  return (
    <div className="rounded-xl border border-white/10 bg-deep px-4 py-3.5">
      <small className="block text-xs text-dim">{label}</small>
      <strong className="mt-0.5 block font-display text-[26px] font-bold tabular-nums tracking-[-0.02em]">{value}</strong>
      <em className="text-xs not-italic text-mist">{note}</em>
    </div>
  );
}

function SidePanels({ positives, keywords }) {
  const covered = keywords.covered || [];
  const missing = keywords.missing || [];
  return (
    <>
      {positives.length ? (
        <div className={PANEL}>
          <p className={PANEL_TITLE}>What is working</p>
          <div className="flex flex-wrap gap-2">
            {positives.map((item) => <span key={item} className={CHIP_OK}>{item}</span>)}
          </div>
        </div>
      ) : null}
      {covered.length || missing.length ? (
        <div className={`${PANEL} ${positives.length ? 'mt-4' : ''}`}>
          <p className={PANEL_TITLE}>Keyword coverage</p>
          <div className="flex flex-wrap gap-2">
            {covered.map((word) => <span key={`ok-${word}`} className={CHIP_OK}>{word}</span>)}
            {missing.map((word) => <span key={`miss-${word}`} className={CHIP_MISS}>{word}</span>)}
          </div>
          <p className="mb-0 mt-3 text-[13px] text-dim">Green is on your site. Dashed is what people search that your site never says.</p>
        </div>
      ) : null}
    </>
  );
}

function NextSteps({ shareUrl, onFallback, onBook }) {
  return (
    <>
      <SectionHead title="What happens next" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className={`${CARD} border-sky/45 bg-gradient-to-b from-cobalt/[0.16] to-deep`}>
          <h4 className={CARD_TITLE}>Fix the top three</h4>
          <p className={CARD_TEXT}>A 20 minute call. We walk through this report and quote the fixes, prices on the page.</p>
          <div className="mt-1"><button type="button" onClick={onBook} className={BTN_PRIMARY}>Book a free call</button></div>
        </div>
        <div className={`${CARD} border-white/10 bg-deep`}>
          <h4 className={CARD_TITLE}>Keep the report</h4>
          <p className={CARD_TEXT}>Share the link with your partner or your web person. It stays the same until you run a new check.</p>
          <div className="mt-1"><CopyButton url={shareUrl} onFallback={onFallback} /></div>
        </div>
        <div className={`${CARD} border-white/10 bg-deep`}>
          <span className="text-[11px] uppercase tracking-[0.08em] text-amber">Coming next · BartuV</span>
          <h4 className={CARD_TITLE}>See your site redesigned</h4>
          <p className={CARD_TEXT}>Two directions of your own site, one built to convert and one built around your brand, ready to click through.</p>
          <div className="mt-1"><button type="button" disabled className={BTN_GHOST}>Reimagine my site</button></div>
        </div>
      </div>
    </>
  );
}

// The finished site check: score, industry floor, top issues (the rest behind
// the email gate), compliance, keywords and what happens next.
export default function SiteCheckReport({ check, onUnlock, unlocking = false, unlockError = '', standalone = false, industries = null, className = '' }) {
  const [booking, setBooking] = useState(false);
  const chipRef = useRef(null);
  const report = check && check.report;
  if (!report) return null;

  const stats = report.stats || {};
  const floor = report.floor || { score: 75, grade: 'B', ev: 'estimated' };
  const industry = industryLabel(check.industryKey, industries);
  const town = townOf(check.city);
  const color = gradeColor(report.grade);
  const companyName = report.companyName || check.domain;
  const pagesRead = stats.pagesRead || 0;
  const shareHost = window.location.host;
  const shareUrl = `${window.location.origin}/check/${check.token}`;
  const meta = [companyName, industry, check.city, formatDate(check.createdAt), `${plural(pagesRead, 'page')} read`].filter(Boolean).join(' · ');
  const summary = report.summary || `${companyName} scores ${report.score} out of 100 for search visibility.`;

  const selectShareLink = () => {
    const node = chipRef.current;
    if (!node || !window.getSelection) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return (
    <section aria-labelledby="site-check-report-title" className={`${standalone ? '' : 'border-t border-white/10 pb-16 pt-12'} ${className}`}>
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <p className="m-0 text-xs uppercase tracking-[0.12em] text-sky">Your website report</p>
          <h2 id="site-check-report-title" className="mb-1.5 mt-2 font-display text-[clamp(30px,4vw,44px)] font-bold leading-[1.05] tracking-[-0.025em] [overflow-wrap:anywhere]">
            {check.domain}
          </h2>
          <p className="m-0 text-sm text-mist">{meta}</p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span ref={chipRef} className="max-w-full select-all rounded-full border border-white/20 bg-deep px-3 py-[7px] font-mono text-[12.5px] text-mist [overflow-wrap:anywhere]">
            {shareHost}/check/{check.token}
          </span>
          <CopyButton url={shareUrl} onFallback={selectShareLink} />
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className={PANEL}>
          <p className={PANEL_TITLE}>Search visibility</p>
          <div className="grid grid-cols-1 justify-items-center gap-[22px] text-center sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:justify-items-stretch sm:text-left">
            <ScoreDial score={report.score} grade={report.grade} animate={!standalone} />
            <div>
              <div className="font-display text-[44px] font-bold leading-none" style={{ color }}>{report.grade}</div>
              <p className="mb-0 mt-2 text-sm text-mist">{summary}</p>
            </div>
          </div>
        </div>
        <div className={PANEL}>
          <p className={PANEL_TITLE}>Industry floor</p>
          <FloorScale score={report.score} grade={report.grade} floor={floor} />
          <p className="m-0 text-base leading-normal">
            {industry} sites{town ? ` in ${town}` : ''} should be at least a{' '}
            <strong className="font-display font-semibold text-sky">{floor.grade}</strong>. Yours is a{' '}
            <strong className="font-display font-semibold" style={{ color }}>{report.grade}</strong>
            {floorVerdict(report.score, floor.score)} <EvTag ev={floor.ev || 'estimated'} />
          </p>
        </div>
      </div>
      {report.notice ? <p className="m-0 mt-3 text-[13px] text-dim">{report.notice}</p> : null}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Issues found" value={stats.issueCount || 0} note={`${stats.criticalCount || 0} critical`} />
        <Kpi label="Homepage load" value={stats.loadMs ? `${(stats.loadMs / 1000).toFixed(1)} s` : 'n/a'} note="goal under 1 s" />
        <Kpi label="Pages read" value={pagesRead} note={`of ${MAX_PAGES}`} />
        <Kpi label="Compliance" value={stats.complianceToFix || 0} note="to fix" />
      </div>

      <SectionHead title="Top issues" />
      <IssueList
        issues={report.issues || []}
        lockedCount={report.lockedCount || 0}
        unlocked={Boolean(check.unlocked)}
        emailed={Boolean(check.emailed)}
        onUnlock={onUnlock}
        unlocking={unlocking}
        unlockError={unlockError}
      />

      <ComplianceSheet rows={report.compliance || []}>
        <SidePanels positives={report.positives || []} keywords={report.keywords || {}} />
      </ComplianceSheet>

      <NextSteps shareUrl={shareUrl} onFallback={selectShareLink} onBook={() => setBooking(true)} />

      <div className="mt-9 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5 text-[13px] text-dim">
        <p className="m-0 max-w-[70ch]">
          Checked by Bluprise. Every finding says how we know it: observed on your site, inferred from public data, estimated, or unknown. Nothing is guessed silently.
        </p>
      </div>
      <BookCallModal open={booking} onClose={() => setBooking(false)} />
    </section>
  );
}
